import axios from "axios";
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { ModalLoader } from "../Components/ModalLoader";
import { Notificacao, NotificacaoComponent } from "../Components/Notificacao";
import { useCartorio } from "../Hooks/cartorio";
import { useFuncionarioCartorio } from "../Hooks/funcionarioCartorio";
import { useFuncionarioPrefeitura } from "../Hooks/funcionarioPrefeitura";
import { useImovel } from "../Hooks/imoveis";
import { usePrefeitura } from "../Hooks/usePrefeitura";
import api from "../Services/api";
import { base_url_unico_api } from "../Utils/baseUrl";
import { isRural } from "../Utils/isRural";
import { Fa500Px } from "react-icons/fa";

// Defina suas interfaces aqui
export interface IAdquirinte {
  ID: string;
  NOME: string;
  RUA: string;
  NUMERO: number;
  BAIRRO: string;
  COMPLEMENTO: string;
  IMOVEL_ID: number;
  IMOVEL_RURAL_ID: number;
  EMAIL: string;
  FONE: string;
  WHATS: string;
  CPF: string;
  CNPJ: string;
  MUNICIPIO: string;
  UF: string;
  COD_UNICO: number;
}

interface IAdquirinteContextData {
  adquirintes: IAdquirinte[];
  setAdquirintes: React.Dispatch<React.SetStateAction<IAdquirinte[]>>;
  getAdquirintes: () => Promise<void>;
  excluir: (id: number) => Promise<void>;
}

interface IAdquirinteProviderProps {
  children: ReactNode;
}

// Crie o contexto
const AdquirinteContext = createContext({} as IAdquirinteContextData);

// Componente provedor
function AdquirinteProvider({ children }: IAdquirinteProviderProps) {
  const { funcionarioCartorioIsLogged } = useFuncionarioCartorio();
  const { funcionarioPrefeituraIsLogged } = useFuncionarioPrefeitura();
  const { cartorioIsLogged } = useCartorio();
  const { prefeituraIsLogged, bancoPrefeitura } = usePrefeitura();
  const [notificacao, setNotificacao] = useState({
    mensagem: "",
    tipo: "AVISO",
  } as Notificacao);

  const [unicoCarregado, setUnicoCarregado] = useState(false);

  const { imovelFocus } = useImovel();

  const [adquirintes, setAdquirintes] = useState([] as IAdquirinte[]);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getAdquirintes();
    
  }, [
    imovelFocus,
    funcionarioCartorioIsLogged,
    funcionarioPrefeituraIsLogged,
    prefeituraIsLogged,
    cartorioIsLogged,
  ]);

  useEffect(() => {
    /* && (prefeituraIsLogged || funcionarioPrefeituraIsLogged) */
  
      if (!unicoCarregado &&adquirintes && adquirintes.length > 0) tentaBuscarUnico();
  }, [adquirintes, prefeituraIsLogged, funcionarioPrefeituraIsLogged, imovelFocus]);

  async function getAdquirintes() {
    try {
      setLoading(true);
      if (imovelFocus.ID) {
        const { data } = isRural()
          ? await api.get(`/adquirinte-rural/${imovelFocus.ID}`)
          : await api.get(`/adquirinte/${imovelFocus.ID}`);
        setAdquirintes(data);
      }
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
      setUnicoCarregado(false)
    }
  }

  async function excluir(id: number) {
    try {
      await api.delete(`/adquirinte/${id}`);
      getAdquirintes();
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  }

  async function tentaBuscarUnico() {
    try {
      setLoading(true);
      if (adquirintes && adquirintes.length > 0) {
        for (let index = 0; index < adquirintes.length; index++) {
          const element = adquirintes[index];
          const { data } = await axios.post(
            `${base_url_unico_api}/adquirinte/check-unico`,
            {
              bancoNum: bancoPrefeitura.trim().substring(0, 1),
              bancoNome: bancoPrefeitura
                .replace("1-", "")
                .replace("2-", "")
                .trim(),
              cpf_cnpj: element.CPF ? element.CPF : element.CNPJ,
            }
          );
          if(data.CODIGO){
             await api.patch(`/adquirinte/atualiza-unico`, {
              id: element.ID,
              unico: data.CODIGO,
            });
            setNotificacao({
              mensagem: `Código único de ${element.NOME} localizado com sucesso!`,
              tipo: "SUCESSO",
            });
          }
          else if (data.length > 0) {
            await api.patch(`/adquirinte/atualiza-unico`, {
              id: element.ID,
              unico: data[0].CODIGO,
            });
            setNotificacao({
              mensagem: `Código único de ${element.NOME} localizado com sucesso!`,
              tipo: "SUCESSO",
            });
          } else {
            setNotificacao({
              mensagem: `OICódigo único de ${element.NOME} não foi localizado!`,
              tipo: "ATENCAO",
            });
          }
        }
      }
         setUnicoCarregado(true);
    } catch (error) {
      // Tratar o erro aqui, se necessário
      setNotificacao({
        mensagem: `Não foi possível localizar os cadastros único!`,
        tipo: "ERRO",
      });
      console.error(error);
    } finally {
      setLoading(false);
   setUnicoCarregado(true);
      getAdquirintes();
    }
  }

  return (
    <AdquirinteContext.Provider
      value={{
        adquirintes,
        setAdquirintes,
        getAdquirintes,
        excluir,
      }}
    >
      <ModalLoader loading={loading} />
      <NotificacaoComponent
        notificacao={notificacao}
        setNotificacao={setNotificacao}
      />
      {children}
    </AdquirinteContext.Provider>
  );
}

// Hook personalizado
function useAdquirinte() {
  const context = useContext(AdquirinteContext);

  if (!context) {
    throw new Error("useAdquirinte must be used within a AdquirinteProvider");
  }

  return context;
}

export { AdquirinteProvider, useAdquirinte };
